import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save, Sparkles, GripVertical, Eye, EyeOff } from "lucide-react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { NameAIDialog } from "@/components/NameAIDialog";
import { CreateGroupDialog } from "@/components/CreateGroupDialog";
import { TelegramSettings } from "@/components/TelegramSettings";
import { useUserRole } from "@/hooks/useUserRole";
import { Bot } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnConfig, ensureDefaults } from "@/lib/columnConfig";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

interface SortableColumnItemProps {
  column: ColumnConfig;
  index: number;
  onUpdate: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  disabled: boolean;
}

function SortableColumnItem({ column, index, onUpdate, onRemove, onToggleVisibility, disabled }: SortableColumnItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isDefault = column.type === 'default';
  const isVisible = column.visible !== false;

  return (
    <div ref={setNodeRef} style={style} className="flex gap-4 items-end">
      <div 
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing flex-shrink-0 p-2 hover:bg-muted rounded self-center mb-2"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1">
        {isDefault ? (
          <>
            <Label className="text-xs text-muted-foreground">Default</Label>
            <div className="h-10 flex items-center px-3 rounded-md border bg-muted/50 text-sm font-medium">
              {column.name}
            </div>
          </>
        ) : (
          <>
            <Label htmlFor={`column-${column.id}`}>
              Custom Column
            </Label>
            <Input
              id={`column-${column.id}`}
              value={column.name}
              onChange={(e) => onUpdate(column.id, e.target.value)}
              placeholder="e.g., Company Size, Industry, Location"
            />
          </>
        )}
      </div>
      {isDefault ? (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggleVisibility(column.id)}
          className="mb-2"
          title={isVisible ? "Hide column" : "Show column"}
        >
          {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onRemove(column.id)}
          disabled={disabled}
          className="mb-2"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

const LeadSetup = () => {
  
  const navigate = useNavigate();
  const { workspace, selectedGroupId, isGroupSwitching } = useWorkspace();
  const { canAccessTelegram } = useUserRole();
  const [columns, setColumns] = useState<ColumnConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reminderDays, setReminderDays] = useState<number>(30);
  const [groupName, setGroupName] = useState<string>("");
  const [aiNameDialogOpen, setAiNameDialogOpen] = useState(false);
  const [aiAssistantName, setAiAssistantName] = useState<string>("AI Assistant");
  const [userRole, setUserRole] = useState<string>("member");
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const configRequestIdRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchColumnConfig();
    if (workspace === "groups" && selectedGroupId) {
      fetchGroupInfo();
    }
  }, [workspace, selectedGroupId]);

  const fetchGroupInfo = async () => {
    if (!selectedGroupId) return;
    
    const user = await getCurrentUser();
    if (!user) return;
    
    const { data } = await requestWithAuthRecovery(() => supabase
      .from("groups")
      .select(`
        name,
        ai_assistant_name,
        group_members!inner(role, user_id)
      `)
      .eq("id", selectedGroupId)
      .single());

    if (data) {
      setGroupName(data.name);
      setAiAssistantName(data.ai_assistant_name || "AI Assistant");
      
      const userMember = (data as any).group_members.find((m: any) => m.user_id === user.id);
      setUserRole(userMember?.role || "member");
    }
  };

  const fetchColumnConfig = async () => {
    const requestId = ++configRequestIdRef.current;
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      if (workspace === "groups" && !selectedGroupId) return;

      if (workspace === "groups" && selectedGroupId) {
        const { data, error } = await requestWithAuthRecovery(() => supabase
          .from("groups")
          .select("column_config, reminder_days, ai_assistant_name")
          .eq("id", selectedGroupId)
          .single());

        if (error) throw error;

        if (requestId !== configRequestIdRef.current) return;
        const config = Array.isArray(data?.column_config) 
          ? data.column_config as unknown as ColumnConfig[]
          : [];
        const seeded = ensureDefaults(config);
        setColumns(seeded.length > 0 ? seeded : ensureDefaults([{ id: crypto.randomUUID(), name: "", type: 'custom' }]));
        setReminderDays(data?.reminder_days || 30);
        setAiAssistantName(data?.ai_assistant_name || "AI Assistant");
      } else {
        const { data, error } = await requestWithAuthRecovery(() => supabase
          .from("profiles")
          .select("personal_column_config, reminder_days, ai_assistant_name")
          .eq("user_id", user.id)
          .single());

        if (error) throw error;

        if (requestId !== configRequestIdRef.current) return;
        const config = Array.isArray(data?.personal_column_config) 
          ? data.personal_column_config as unknown as ColumnConfig[]
          : [];
        const seeded = ensureDefaults(config);
        setColumns(seeded.length > 0 ? seeded : ensureDefaults([{ id: crypto.randomUUID(), name: "", type: 'custom' }]));
        setReminderDays(data?.reminder_days || 30);
        setAiAssistantName(data?.ai_assistant_name || "AI Assistant");
      }
    } catch (error: any) {
      toast.error("Error loading configuration", { description: error.message });
    } finally {
      if (requestId === configRequestIdRef.current) setLoading(false);
    }
  };

  const addColumn = () => {
    setColumns([...columns, { id: crypto.randomUUID(), name: "", type: 'custom', visible: true }]);
  };

  const removeColumn = (id: string) => {
    setColumns(columns.filter(col => col.id !== id));
  };

  const updateColumnName = (id: string, name: string) => {
    setColumns(columns.map(col => col.id === id ? { ...col, name } : col));
  };

  const toggleVisibility = (id: string) => {
    setColumns(columns.map(col => col.id === id ? { ...col, visible: col.visible === false ? true : false } : col));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columns.findIndex((c) => c.id === active.id);
      const newIndex = columns.findIndex((c) => c.id === over.id);

      const newColumns = arrayMove(columns, oldIndex, newIndex);
      setColumns(newColumns);

      try {
        const user = await getCurrentUser();
        if (!user) return;

        if (workspace === "groups" && selectedGroupId) {
          await supabase
            .from("groups")
            .update({ column_config: newColumns as any })
            .eq("id", selectedGroupId);
        } else {
          await supabase
            .from("profiles")
            .update({ personal_column_config: newColumns as any })
            .eq("user_id", user.id);
        }

        toast.success("Column order updated", { description: "Your column order has been saved." });
      } catch (error: any) {
        toast.error("Error saving order", { description: error.message });
      }
    }
  };

  const saveConfiguration = async () => {
    setSaving(true);
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      // For saving, keep all columns (including defaults with visibility state)
      // Only filter out empty custom columns
      const validColumns = columns.filter(col => col.type === 'default' || col.name.trim() !== "");

      // Fetch old column config to detect renamed columns
      let oldColumns: ColumnConfig[] = [];
      if (workspace === "groups" && selectedGroupId) {
        const { data } = await supabase
          .from("groups")
          .select("column_config")
          .eq("id", selectedGroupId)
          .single();
        oldColumns = Array.isArray(data?.column_config) ? data.column_config as unknown as ColumnConfig[] : [];
      } else {
        const { data } = await supabase
          .from("profiles")
          .select("personal_column_config")
          .eq("user_id", user.id)
          .single();
        oldColumns = Array.isArray(data?.personal_column_config) ? data.personal_column_config as unknown as ColumnConfig[] : [];
      }

      // Detect renamed columns (same ID, different name) - only for custom columns
      const renamedColumns: { oldName: string; newName: string }[] = [];
      validColumns.forEach(newCol => {
        if (newCol.type === 'default') return; // defaults can't be renamed
        const oldCol = oldColumns.find(old => old.id === newCol.id);
        if (oldCol && oldCol.name !== newCol.name && oldCol.name.trim() !== "") {
          renamedColumns.push({ oldName: oldCol.name, newName: newCol.name });
        }
      });

      // If columns were renamed, migrate the data in all leads
      if (renamedColumns.length > 0) {
        const { data: leads } = await supabase
          .from("leads")
          .select("id, custom_fields")
          .or(
            workspace === "groups" && selectedGroupId
              ? `group_id.eq.${selectedGroupId}`
              : `user_id.eq.${user.id},group_id.is.null`
          );

        if (leads && leads.length > 0) {
          for (const lead of leads) {
            const customFields = lead.custom_fields || {};
            let updated = false;

            renamedColumns.forEach(({ oldName, newName }) => {
              if (customFields[oldName] !== undefined) {
                customFields[newName] = customFields[oldName];
                delete customFields[oldName];
                updated = true;
              }
            });

            if (updated) {
              await supabase
                .from("leads")
                .update({ custom_fields: customFields })
                .eq("id", lead.id);
            }
          }
        }
      }

      if (workspace === "groups" && selectedGroupId) {
        const { error } = await supabase
          .from("groups")
          .update({ 
            column_config: validColumns as any,
            reminder_days: reminderDays 
          })
          .eq("id", selectedGroupId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("profiles")
          .update({ 
            personal_column_config: validColumns as any,
            reminder_days: reminderDays 
          })
          .eq("user_id", user.id);

        if (error) throw error;
      }

      toast.success("Configuration saved", { description: `Your ${workspace} lead sheet columns have been updated.${renamedColumns.length > 0 ? ' Data migrated successfully.' : ''}` });
    } catch (error: any) {
      toast.error("Error saving configuration", { description: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const customColumns = columns.filter(c => c.type !== 'default');

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {isGroupSwitching ? (
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Switching groups...</p>
        </div>
      ) : workspace === "groups" && !selectedGroupId ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-semibold">No Group Selected</h2>
            <p className="text-muted-foreground">Create or join a group to get started</p>
          </div>
          <Button onClick={() => setCreateGroupDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Your First Group
          </Button>
        </div>
      ) : (
        <>
          {workspace === "groups" && groupName && (
            <h2 className="text-4xl font-bold mb-6 text-center">{groupName}</h2>
          )}
          <Card>
        <CardHeader>
          <CardTitle>Lead Sheet Setup - {workspace === "personal" ? "Personal" : "Groups"}</CardTitle>
          <CardDescription>
            Configure columns for your {workspace} lead sheets. Drag to reorder, toggle visibility on default columns, and add custom columns below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">All Columns</h3>
                  {columns.map((column, index) => (
                    <SortableColumnItem
                      key={column.id}
                      column={column}
                      index={index}
                      onUpdate={updateColumnName}
                      onRemove={removeColumn}
                      onToggleVisibility={toggleVisibility}
                      disabled={customColumns.length <= 1 && column.type !== 'default'}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={addColumn} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Custom Column
            </Button>
            <Button onClick={saveConfiguration} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="reminder-days">Remind me every (days)</Label>
              <Input
                id="reminder-days"
                type="number"
                min="1"
                value={reminderDays}
                onChange={(e) => setReminderDays(Number(e.target.value))}
                className="w-32 mt-1"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Leads will appear in the Refresher tab after this many days of inactivity
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>AI Assistant Name</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAiNameDialogOpen(true)}
                  className="gap-2"
                  disabled={workspace === "groups" && !["leader", "admin"].includes(userRole)}
                >
                  <Sparkles className="h-4 w-4" />
                  Customize Name
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Current name: <span className="font-medium">{aiAssistantName}</span>
              </p>
              {workspace === "groups" && !["leader", "admin"].includes(userRole) && (
                <p className="text-xs text-muted-foreground mt-1">
                  Only Leaders and Admins can change the AI assistant name
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">How it works:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Drag columns to reorder them, default and custom columns can be interleaved</li>
              <li>Toggle the eye icon to show/hide default columns</li>
              <li>Add custom columns to track specific information about your leads</li>
              <li>Tell the AI assistant about a lead naturally, and it will organize the data</li>
              <li>Set reminder days to automatically track leads that need follow-up</li>
            </ul>
          </div>
          </CardContent>
        </Card>

        {canAccessTelegram && (workspace === "personal" || selectedGroupId) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                {workspace === "personal" ? "Personal Telegram Bot" : "Group Telegram Bot"}
              </CardTitle>
              <CardDescription>
                Connect a Telegram bot for this workspace to add and update leads from chat.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TelegramSettings
                groupId={workspace === "groups" ? selectedGroupId : null}
                isAdmin={workspace === "personal" || ["leader", "admin"].includes(userRole)}
              />
            </CardContent>
          </Card>
        )}

        <NameAIDialog
          open={aiNameDialogOpen}
          onOpenChange={setAiNameDialogOpen}
          currentName={aiAssistantName}
          isGroupWorkspace={workspace === "groups"}
          groupId={selectedGroupId}
          onNameUpdated={() => {
            fetchColumnConfig();
            if (workspace === "groups" && selectedGroupId) {
              fetchGroupInfo();
            }
          }}
        />
        </>
      )}

      <CreateGroupDialog
        open={createGroupDialogOpen}
        onOpenChange={setCreateGroupDialogOpen}
        onGroupCreated={(groupId) => {
          navigate(`/dashboard/groups/${groupId}/setup`);
        }}
      />
    </div>
  );
};

export default LeadSetup;
